import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withKeys as _withKeys, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "flex items-center gap-2" };
const _hoisted_2 = { class: "text-base font-medium leading-6 text-(--text-text-black-default)" };
const _hoisted_3 = { class: "flex gap-4 version-history-content" };
const _hoisted_4 = { class: "w-3/5 min-w-0 overflow-hidden rounded-lg border border-(--border-border-base-soft) bg-(--background-background-white)" };
const _hoisted_5 = {
    key: 0,
    class: "flex h-full items-center justify-center p-8 text-(--text-text-body)"
};
const _hoisted_6 = ["srcdoc", "title"];
const _hoisted_7 = {
    key: 2,
    class: "flex h-full items-center justify-center p-8 text-center text-(--text-text-body)"
};
const _hoisted_8 = { class: "w-2/5 min-w-0 overflow-y-auto rounded-lg border border-(--border-border-base-soft) bg-(--background-background-white)" };
const _hoisted_9 = {
    key: 0,
    class: "flex h-full items-center justify-center p-8 text-(--text-text-body)"
};
const _hoisted_10 = {
    key: 1,
    class: "flex h-full items-center justify-center p-8 text-center text-(--text-text-body)"
};
const _hoisted_11 = {
    key: 2,
    class: "flex h-full items-center justify-center p-8 text-center text-(--text-text-error)"
};
const _hoisted_12 = ["aria-label"];
const _hoisted_13 = ["aria-selected", "onClick", "onKeydown"];
const _hoisted_14 = { class: "flex items-start justify-between gap-2" };
const _hoisted_15 = { class: "min-w-0 flex-1" };
const _hoisted_16 = { class: "text-sm font-medium text-(--text-text-black-default)" };
const _hoisted_17 = { class: "mt-0.5 truncate text-xs text-(--text-text-body)" };
const _hoisted_18 = { class: "flex flex-shrink-0 flex-col items-end gap-1" };
const _hoisted_19 = {
    key: 4,
    class: "border-t border-(--border-border-base-soft) p-3 text-center"
};
import { ref, computed, watch, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { Modal, Button, Badge, useToast } from "@eudonet/opal";
import { useTemplatesStore } from "../../stores/useTemplatesStore";
export default /*@__PURE__*/ _defineComponent({
    __name: 'VersionHistoryModal',
    props: {
        modelValue: { type: Boolean },
        templateId: {},
        kind: { default: "template" },
        provider: { default: undefined }
    },
    emits: ["update:modelValue", "restored"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t, locale } = useI18n();
        const { showToast } = useToast();
        const templatesStore = useTemplatesStore();
        // Default (template) provider built from the template store when no provider is supplied.
        const defaultTemplateProvider = {
            async listVersions(id, page, pageSize) {
                // Delegate through the store to preserve cache & error state semantics.
                await templatesStore.fetchVersions(id, page);
                return {
                    items: templatesStore.versions,
                    page: templatesStore.versionsPagination.page,
                    pageSize: templatesStore.versionsPagination.pageSize ?? pageSize,
                    totalCount: templatesStore.versionsPagination.totalCount,
                    totalPages: templatesStore.versionsPagination.totalPages,
                };
            },
            async getVersionContent(id, versionId) {
                return await templatesStore.fetchVersionContent(id, versionId);
            },
            async restoreVersion(id, versionId) {
                await templatesStore.restoreVersion(id, versionId);
            },
            getLatestHtmlContent: (id) => templatesStore.currentTemplate?.id === id ? templatesStore.currentTemplate?.htmlContent : null,
        };
        const activeProvider = computed(() => props.provider ?? defaultTemplateProvider);
        const versions = ref([]);
        const pagination = reactive({ page: 1, pageSize: 20, totalCount: 0, totalPages: 0 });
        const loadingList = ref(false);
        const listError = ref(null);
        const selectedVersion = ref(null);
        const restoring = ref(false);
        const versionContentCache = ref(new Map());
        const loadingPreview = ref(false);
        const dateFormatter = computed(() => new Intl.DateTimeFormat(locale.value, {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        }));
        const hasMorePages = computed(() => pagination.page < pagination.totalPages);
        const latestVersion = computed(() => {
            if (versions.value.length === 0)
                return null;
            return versions.value.reduce((latest, v) => (v.versionNumber > latest.versionNumber ? v : latest), versions.value[0]);
        });
        const previewHtml = computed(() => {
            if (!selectedVersion.value)
                return null;
            return versionContentCache.value.get(selectedVersion.value.id) ?? null;
        });
        const canRestore = computed(() => {
            if (!selectedVersion.value || restoring.value)
                return false;
            return !isLatestVersion(selectedVersion.value);
        });
        function isLatestVersion(version) {
            return latestVersion.value?.id === version.id;
        }
        async function loadPage(page) {
            loadingList.value = true;
            listError.value = null;
            try {
                const result = await activeProvider.value.listVersions(props.templateId, page, pagination.pageSize);
                versions.value = page === 1 ? result.items : [...versions.value, ...result.items];
                pagination.page = result.page;
                pagination.pageSize = result.pageSize;
                pagination.totalCount = result.totalCount;
                pagination.totalPages = result.totalPages;
            }
            catch {
                listError.value = t("templating.error.loadVersionsFailed");
            }
            finally {
                loadingList.value = false;
            }
        }
        async function selectVersion(version) {
            selectedVersion.value = version;
            if (versionContentCache.value.has(version.id))
                return;
            // Latest version: short-circuit via provider if available
            if (isLatestVersion(version)) {
                const latestHtml = activeProvider.value.getLatestHtmlContent?.(props.templateId);
                if (latestHtml) {
                    versionContentCache.value.set(version.id, latestHtml);
                    return;
                }
            }
            loadingPreview.value = true;
            try {
                const detail = await activeProvider.value.getVersionContent(props.templateId, version.id);
                if (detail.htmlContent) {
                    versionContentCache.value.set(version.id, detail.htmlContent);
                }
            }
            finally {
                loadingPreview.value = false;
            }
        }
        async function loadMore() {
            await loadPage(pagination.page + 1);
        }
        async function handleRestore() {
            if (!selectedVersion.value || !canRestore.value)
                return;
            restoring.value = true;
            try {
                await activeProvider.value.restoreVersion(props.templateId, selectedVersion.value.id);
                showToast({
                    message: t("templating.versionHistory.restoreSuccess"),
                    ariaClose: t("form.dismissToast"),
                    status: "success",
                    duration: 3000,
                });
                emit("update:modelValue", false);
                emit("restored");
            }
            catch {
                showToast({
                    message: t("templating.versionHistory.restoreError"),
                    ariaClose: t("form.dismissToast"),
                    status: "error",
                });
            }
            finally {
                restoring.value = false;
            }
        }
        function formatRelativeTime(dateString) {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now.getTime() - date.getTime();
            const diffMinutes = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            if (diffMinutes < 1) {
                return t("templating.versionHistory.timeAgo.justNow");
            }
            if (diffMinutes < 60) {
                return t("templating.versionHistory.timeAgo.minutesAgo", diffMinutes);
            }
            if (diffHours < 24) {
                return t("templating.versionHistory.timeAgo.hoursAgo", diffHours);
            }
            return dateFormatter.value.format(date);
        }
        // Fetch versions when modal opens
        watch(() => props.modelValue, async (open) => {
            if (open) {
                selectedVersion.value = null;
                versionContentCache.value = new Map();
                loadingPreview.value = false;
                versions.value = [];
                pagination.page = 1;
                pagination.totalCount = 0;
                pagination.totalPages = 0;
                await loadPage(1);
                if (versions.value.length > 0 && latestVersion.value) {
                    await selectVersion(latestVersion.value);
                }
            }
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                "model-value": __props.modelValue,
                size: "lg",
                "show-footer": true,
                "footer-position": "right",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (_ctx.$emit('update:modelValue', $event)))
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _cache[1] || (_cache[1] = _createElementVNode("i", { class: "ri-history-line text-base text-(--text-text-black-default)" }, null, -1 /* CACHED */)),
                        _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(t)("templating.versionHistory.title")), 1 /* TEXT */)
                    ])
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        size: "sm",
                        label: restoring.value
                            ? _unref(t)('templating.versionHistory.restoring')
                            : _unref(t)('templating.versionHistory.restore'),
                        "icon-left": "ri-arrow-go-back-line",
                        disabled: !canRestore.value,
                        loading: restoring.value,
                        onClick: handleRestore
                    }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("div", _hoisted_4, [
                            (loadingPreview.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[2] || (_cache[2] = [
                                        _createElementVNode("i", { class: "ri-loader-4-line animate-spin text-xl" }, null, -1 /* CACHED */)
                                    ]))]))
                                : (previewHtml.value)
                                    ? (_openBlock(), _createElementBlock("iframe", {
                                        key: 1,
                                        srcdoc: previewHtml.value,
                                        class: "h-full w-full border-0",
                                        sandbox: "allow-same-origin",
                                        title: _unref(t)('templating.versionHistory.preview')
                                    }, null, 8 /* PROPS */, _hoisted_6))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                        _createElementVNode("p", null, _toDisplayString(_unref(t)("templating.versionHistory.previewNotAvailable")), 1 /* TEXT */)
                                    ]))
                        ]),
                        _createElementVNode("div", _hoisted_8, [
                            (loadingList.value && versions.value.length === 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [...(_cache[3] || (_cache[3] = [
                                        _createElementVNode("i", { class: "ri-loader-4-line animate-spin text-xl" }, null, -1 /* CACHED */)
                                    ]))]))
                                : (versions.value.length === 0)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                        _createElementVNode("p", null, _toDisplayString(_unref(t)("templating.versionHistory.empty")), 1 /* TEXT */)
                                    ]))
                                    : (listError.value)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                            _createElementVNode("p", null, _toDisplayString(listError.value), 1 /* TEXT */)
                                        ]))
                                        : (_openBlock(), _createElementBlock("ul", {
                                            key: 3,
                                            role: "listbox",
                                            "aria-label": _unref(t)('templating.versionHistory.title'),
                                            class: "divide-y divide-(--border-border-base-soft)"
                                        }, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(versions.value, (version) => {
                                                return (_openBlock(), _createElementBlock("li", {
                                                    key: version.id,
                                                    role: "option",
                                                    tabindex: "0",
                                                    "aria-selected": selectedVersion.value?.id === version.id,
                                                    class: _normalizeClass(["cursor-pointer px-4 py-3 transition-colors hover:bg-(--background-background-hover)", { 'bg-(--background-background-hover)': selectedVersion.value?.id === version.id }]),
                                                    onClick: ($event) => (selectVersion(version)),
                                                    onKeydown: [
                                                        _withKeys(($event) => (selectVersion(version)), ["enter"]),
                                                        _withKeys(_withModifiers(($event) => (selectVersion(version)), ["prevent"]), ["space"])
                                                    ]
                                                }, [
                                                    _createElementVNode("div", _hoisted_14, [
                                                        _createElementVNode("div", _hoisted_15, [
                                                            _createElementVNode("p", _hoisted_16, _toDisplayString(_unref(t)("templating.versionHistory.versionLabel", { number: version.versionNumber })) + " · " + _toDisplayString(formatRelativeTime(version.createdAt)), 1 /* TEXT */),
                                                            _createElementVNode("p", _hoisted_17, _toDisplayString(version.createdByName ?? _unref(t)("templating.versionHistory.version")), 1 /* TEXT */)
                                                        ]),
                                                        _createElementVNode("div", _hoisted_18, [
                                                            (isLatestVersion(version))
                                                                ? (_openBlock(), _createBlock(_unref(Badge), {
                                                                    key: 0,
                                                                    theme: "indigo",
                                                                    size: "sm",
                                                                    label: _unref(t)('templating.versionHistory.currentVersion'),
                                                                    "show-left-icon": false,
                                                                    "show-close-icon": false
                                                                }, null, 8 /* PROPS */, ["label"]))
                                                                : _createCommentVNode("v-if", true),
                                                            (version.isPublished)
                                                                ? (_openBlock(), _createBlock(_unref(Badge), {
                                                                    key: 1,
                                                                    theme: "green",
                                                                    size: "sm",
                                                                    label: _unref(t)('templating.versionHistory.published'),
                                                                    "show-left-icon": false,
                                                                    "show-close-icon": false
                                                                }, null, 8 /* PROPS */, ["label"]))
                                                                : _createCommentVNode("v-if", true)
                                                        ])
                                                    ])
                                                ], 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_13));
                                            }), 128 /* KEYED_FRAGMENT */))
                                        ], 8 /* PROPS */, _hoisted_12)),
                            (hasMorePages.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                    _createVNode(_unref(Button), {
                                        variant: "tertiary",
                                        size: "sm",
                                        label: _unref(t)('templating.versionHistory.loadMore'),
                                        loading: loadingList.value,
                                        disabled: loadingList.value,
                                        onClick: loadMore
                                    }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                                ]))
                                : _createCommentVNode("v-if", true)
                        ])
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["model-value"]));
        };
    }
});
